import { z } from 'zod';

/**
 * Projektaufgaben (PM-App-Integration Phase A, ADR 0050): Aufgabenliste je
 * Projekt mit Zuständigem (Benutzer-Kürzel), Deadline, Status und Sortierung.
 */

export const projectTaskStatusSchema = z.enum(['OPEN', 'DONE']);
export type ProjectTaskStatus = z.infer<typeof projectTaskStatusSchema>;

export const createProjectTaskSchema = z.object({
  title: z.string().trim().min(1).max(300),
  note: z.string().trim().max(2000).optional(),
  /** Zuständige (Mehrfach, ADR 0100) — leeres Array = niemand zugewiesen. */
  assigneeUserIds: z
    .array(z.string().uuid())
    .max(20)
    .refine((ids) => new Set(ids).size === ids.length, 'Ein Benutzer kann nicht mehrfach zugewiesen werden.')
    .optional(),
  /** ISO-Datum (YYYY-MM-DD) oder null. */
  deadline: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
});
export type CreateProjectTaskInput = z.infer<typeof createProjectTaskSchema>;

export const updateProjectTaskSchema = createProjectTaskSchema.partial().extend({
  status: projectTaskStatusSchema.optional(),
});
export type UpdateProjectTaskInput = z.infer<typeof updateProjectTaskSchema>;

/** Vollständige Liste der Aufgaben-IDs in gewünschter Reihenfolge (DnD). */
export const reorderProjectTasksSchema = z.object({
  orderedIds: z.array(z.string().uuid()).min(1).max(1000),
});
export type ReorderProjectTasksInput = z.infer<typeof reorderProjectTasksSchema>;

export const projectTaskResponseSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  title: z.string(),
  note: z.string().nullable(),
  status: projectTaskStatusSchema,
  assigneeUserIds: z.array(z.string().uuid()),
  /** Kürzel-Snapshot der Zuständigen (Anzeige/PDF), gleiche Reihenfolge wie die IDs. */
  assigneeInitials: z.array(z.string()),
  deadline: z.string().nullable(),
  completedAt: z.string().nullable(),
  position: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ProjectTaskResponse = z.infer<typeof projectTaskResponseSchema>;

/**
 * Eine dem aktuellen Nutzer zugewiesene, offene Aufgabe inkl. Projektkontext
 * (modulübergreifende „Meine Aufgaben"-Liste im Dashboard, ADR 0062). Erweitert
 * die normale Aufgaben-Antwort um Code/Name des Projekts fürs Anzeigen + Verlinken.
 */
export const myProjectTaskResponseSchema = projectTaskResponseSchema.extend({
  projectCode: z.string(),
  projectName: z.string(),
});
export type MyProjectTaskResponse = z.infer<typeof myProjectTaskResponseSchema>;

/** Dashboard-Kennzahlen über alle Projekte. */
export const projectTaskSummarySchema = z.object({
  open: z.number().int(),
  /** Offen mit Deadline in den nächsten 7 Tagen (inkl. überfällig). */
  dueSoon: z.number().int(),
  overdue: z.number().int(),
});
export type ProjectTaskSummary = z.infer<typeof projectTaskSummarySchema>;

// ── Vorlage „Standard-Aufgaben" (Admin) ──────────────────────────────────────
export const projectTaskTemplateSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  note: z.string().nullable(),
  position: z.number().int(),
  isActive: z.boolean(),
});
export type ProjectTaskTemplate = z.infer<typeof projectTaskTemplateSchema>;
